import { FontIcon, IBreadcrumbItem, IBreadcrumbProps, PrimaryButton, Stack } from "@fluentui/react";
import { makeStyles } from "@fluentui/react-components";
import Box from "@mui/material/Box";
import { models } from "powerbi-client";
import { PowerBIEmbed } from "powerbi-client-react";
import { useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { logPageView } from "../helper/AppInsightsHelper";
import { CustomBreadcrumb } from "../SharedComponents/CustomBreadcrumb";
import { useAppSelector } from "../store/hooks";

const mainCardStyles = {
  "--font-family": "\"Roboto\", \"Helvetica\", \"Arial\", sans-serif",
  margin: 0
};

const pbiReportStyles = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -43%)",
  width: "100%",
  bgcolor: "background.paper",
  height: "100%"
};

const breadcrumbStyles = {
  root: {
    fontSize: 14,
    margin: 0,
    paddingLeft: 8,
    "--itemLink-color": "rgb(0, 120, 212)"
  },
  item: {
    fontSize: "inherit"
  },
  itemLink: {
    fontSize: "inherit",
    color: "var(--itemLink-color)",
    ":hover": {
      background: "none",
      textDecoration: "underline",
      color: "var(--itemLink-color)"
    }
  }
};

const useEmbedPowerBiStyles = makeStyles({
  powerBi: {
    "& iframe": {
      border: "none"
    },
    flexGrow: 1,
    height: "100%"
  }
});

const key = "support-report-view-bc";

export default function SupportReportView() {
  const styles = useEmbedPowerBiStyles();
  const { reportId } = useParams<{ reportId: string }>();
  const formattedReportId = reportId?.split("=")[1];
  const location = useLocation();
  const { reportName, reportLink, category } = location.state || {};
  const navigate = useNavigate();
  const powerbiToken = useAppSelector((state) => state.auth.powerbiToken);

  useEffect(() => {
    const customProps = {
      ReportId: formattedReportId,
      ReportName: reportName || "",
      Category: category || "",
      ReportLink: reportLink || ""
    };
    logPageView("Support Report View Page", window.location.href, customProps);
  }, [formattedReportId, reportName, category, reportLink]);

  const items: IBreadcrumbItem[] = [
    { text: "Home", key: "home", onClick: () => navigate("/") },
    {
      text: "Explore Reports",
      key: "explore-products-list",
      onClick: () => navigate("/explorereports?tab=supportCatalog")
    },
    { text: category || "", key: "supportCatalog", onClick: () => navigate(`/supportreports?category=${category}`) },
    { text: reportName, key: "reportName", isCurrentItem: true }
  ];

  const breadcrumbProps: IBreadcrumbProps = {
    items: items,
    styles: breadcrumbStyles
  };

  const closeReport = () => {
    // For MCS reports, redirect back to MCS page
    if (category === "MCS") {
      navigate("/supportreports?category=MCS");
    } else {
      navigate("/explorereports?tab=supportCatalog");
    }
  };

  window.onerror = function () {
    // Do nothing in this case
    // This error is thrown by PowerBI client library when user don't have access to the report
    // or if there is any issue in opening the report.
    // This is not a critical or blocking error for Security360, so we can ignore it.

    return false;
  };

  return (
    <div className="mainCard" style={mainCardStyles}>
      <Stack horizontal style={{ position: "relative" }}>
        <CustomBreadcrumb {...breadcrumbProps} key={key} />
        <PrimaryButton
          style={{
            position: "absolute",
            right: 140,
            marginRight: "10px",
            marginTop: "10px"
          }}
          text="Open In New Window"
          type="a"
          href={reportLink}
          target="_blank"
        >
          <FontIcon
            aria-label="OpenInNewWindowButton"
            iconName="OpenInNewWindow"
            style={{ fontSize: "20px", marginLeft: "5px" }}
          />
        </PrimaryButton>
        <PrimaryButton
          style={{
            position: "absolute",
            right: 30,
            marginRight: "10px",
            marginTop: "10px"
          }}
          text="Close"
          onClick={() => {
            closeReport();
          }}
        >
          <FontIcon
            aria-label="ReportCloseButton"
            iconName="ChromeClose"
            style={{ fontSize: "10px", marginLeft: "5px" }}
          />
        </PrimaryButton>
      </Stack>
      <Box sx={pbiReportStyles}>
        <PowerBIEmbed
          cssClassName={styles.powerBi}
          embedConfig={{
            type: "report",
            id: formattedReportId,
            embedUrl: `https://msit.powerbi.com/reportEmbed?reportId=${formattedReportId}`,
            accessToken: powerbiToken,
            tokenType: 0,
            settings: {
              customLayout: {
                displayOption: models.DisplayOption.FitToPage
              },
              panes: {
                pageNavigation: {
                  position: models.PageNavigationPosition.Left
                }
              }
            }
          }}
        />
      </Box>
    </div>
  );
}
